import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  Bell,
  Soup,
  UtensilsCrossed,
  Croissant,
  IceCreamCone,
  CupSoda,
  Utensils,
  Coffee,
  Salad,
  Beef,
  Wheat,
  Pizza,
  Sandwich,
  CakeSlice,
  Wine,
  Clock,
  MapPin,
  X,
  type LucideIcon
} from 'lucide-react';
import { screenService, orderService, settingsService, unprotectParam, type QrMenuScreen, type MenuItem, type ApiKitchenStatus } from '@kss/api';
import { useAuth } from '@kss/auth';
import { useConfig } from '@kss/theme';
import CustomerInfoModal from '../components/CustomerInfoModal';
import MapPicker from '../components/MapPicker';
import { useCurrency } from '@kss/currency';
import {
  Card,
  Chip,
  VegMark,
  Button,
  QtyStepper,
  PriceTag,
  ItemDetailModal,
  useCart,
  useToast,
  useCallWaiter,
  useOrderSession,
  useTable,
  cn,
  FieldError,
  useField,
  v,
  applyGuard,
  onlyDigits,
  type OrderType
} from '@kss/ui';
import { useT } from '../i18n/i18n';
import { formatTable } from '../lib/format';

/** Category code → icon (falls back to a generic utensils glyph for unknown ids). */
const CATEGORY_ICON: Record<string, LucideIcon> = {
  breakfast: Coffee,
  soups: Soup,
  'starters-veg': Salad,
  'starters-nonveg': Beef,
  'maincourse-veg': UtensilsCrossed,
  'maincourse-nonveg': Beef,
  rice: Wheat,
  breads: Croissant,
  chinese: Soup,
  pizza: Pizza,
  burgers: Sandwich,
  sandwiches: Sandwich,
  pasta: Wheat,
  salads: Salad,
  desserts: CakeSlice,
  beverages: CupSoda,
  icecream: IceCreamCone,
  mocktails: Wine,
  starters: Salad,
  mains: UtensilsCrossed
};

/** Reverse-geocode a lat/lng to a human address via OpenStreetMap Nominatim (keyless, CORS-ok).
 *  Returns null on any failure so callers can fall back to raw coordinates. */
async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=jsonv2&lat=${lat}&lon=${lng}&addressdetails=1`,
      { headers: { Accept: 'application/json' } }
    );
    const data = await res.json();
    return (data && typeof data.display_name === 'string' && data.display_name) || null;
  } catch {
    return null;
  }
}

/**
 * The bare 10-digit mobile the order API expects, from however the number is currently held.
 *
 * Sign-in hands back a DISPLAY-formatted phone ("+91 9876543210") and that is what lands in the
 * auth store, so the raw value cannot be posted back. Taking the last 10 digits also survives a
 * number typed with spaces, a leading 0, or +91.
 */
function orderPhone(value: string): string {
  return (value || '').replace(/\D/g, '').slice(-10);
}

/** Order fulfilment types (per design). */
const ORDER_TYPES: { value: OrderType; label: string }[] = [
  { value: 'dine-in', label: 'Dine-in' },
  { value: 'takeaway', label: 'Takeaway' },
  { value: 'packed', label: 'Packed' },
  { value: 'delivery', label: 'Delivery' }
];

export default function QrMenu() {
  const tableCode = useTable(); // current dine-in table from the session (set by the QR scan)
  const [data, setData] = useState<QrMenuScreen | null>(null);
  // 'all' (default) shows every category stacked one below another; a specific id filters to it.
  const [cat, setCat] = useState('all');
  const { user, isAuthenticated } = useAuth();
  // We have what an order needs only once the customer is signed in AND has a real name (not the
  // "Guest" placeholder). Otherwise the capture prompt appears when they take a new order — so a
  // customer whose name is still missing is asked for it (name is required), but a complete one
  // is never re-asked.
  const customerName = (user?.name ?? '').trim();
  const hasCustomerDetails = isAuthenticated && customerName.length > 0 && customerName.toLowerCase() !== 'guest';
  // Petpooja flow: guests browse freely, but adding to the order / placing it asks for
  // name + mobile (OTP) the FIRST time only. What they were doing is stashed in `pendingRef`
  // (a ref, so it survives the sign-in re-render) and resumed the moment they're signed in.
  const [infoOpen, setInfoOpen] = useState(false);
  const pendingRef = useRef<{ action: 'add' | 'place'; item?: MenuItem } | null>(null);
  // Kitchen open/close gate — customers can only order while the kitchen is open (day-wise hours).
  const [kitchen, setKitchen] = useState<ApiKitchenStatus>({ open: true, hoursToday: '', message: '' });
  const { brand } = useConfig();
  const { format } = useCurrency();
  const cart = useCart();
  const orderSession = useOrderSession();
  const toast = useToast();
  const t = useT();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const categoryParam = searchParams.get('category');

  // No scanned QR ⇒ not seated at a table ⇒ this is a takeaway order (not a phantom "Table 7").
  const hasTable = tableCode.length > 0;
  const [orderType, setOrderType] = useState<OrderType>('dine-in');
  const [selected, setSelected] = useState<MenuItem | null>(null);
  const [sheetOpen, setSheetOpen] = useState(false);
  // Home-delivery details — collected before a delivery order can be placed. `lat`/`lng` are the
  // map-pin drop point (null until the customer uses GPS or moves the pin). Only the address is
  // required — contact + email are optional (the order still scopes to the signed-in customer).
  const [deliveryOpen, setDeliveryOpen] = useState(false);
  // True when the delivery form was opened from "Place order" (so submit places the order); false
  // when opened by selecting the Delivery tab (submit just saves the address for later).
  const [deliveryThenPlace, setDeliveryThenPlace] = useState(false);
  const [delivery, setDelivery] = useState<{
    contact: string;
    address: string;
    email: string;
    lat: number | null;
    lng: number | null;
  }>({ contact: '', address: '', email: '', lat: null, lng: null });
  const [geoBusy, setGeoBusy] = useState(false);

  // Delivery details go onto a real order that a rider has to act on, so they get the same rules
  // the API applies. The gate below previously only checked "address non-empty, 10+ digits",
  // which let through a 15-digit number, a landline and an unparseable email.
  const addressField = useField(delivery.address, [
    v.required('Delivery address'),
    v.minLength(10, 'Delivery address'),
    v.maxLength(250, 'Delivery address'),
    ...v.safeText('Delivery address')
  ]);
  const contactField = useField(delivery.contact, [
    v.required('Contact number'),
    v.mobile('Contact number')
  ]);
  const emailField = useField(delivery.email, [v.email('Email'), v.maxLength(120, 'Email')]);
  const deliveryValid = addressField.isValid && contactField.isValid && emailField.isValid;

  // Prefill the delivery form from the customer's last delivery (so a returning customer sees
  // their saved address + pin instead of an empty form).
  useEffect(() => {
    try {
      const saved = localStorage.getItem('thalios.delivery');
      if (saved) {
        const d = JSON.parse(saved);
        setDelivery((prev) => ({
          contact: d.contact ?? prev.contact,
          address: d.address ?? prev.address,
          email: d.email ?? prev.email,
          lat: typeof d.lat === 'number' ? d.lat : prev.lat,
          lng: typeof d.lng === 'number' ? d.lng : prev.lng
        }));
      }
    } catch {
      /* corrupt/absent → start blank */
    }
  }, []);

  // Auto-fill the delivery address from the device's GPS location (browser geolocation +
  // OpenStreetMap reverse-geocoding). Captures the lat/lng so the map pin + drop point follow.
  const useMyLocation = () => {
    if (!('geolocation' in navigator)) {
      toast.show('Location is not supported on this device', 'error');
      return;
    }
    setGeoBusy(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        const coords = `${latitude.toFixed(6)}, ${longitude.toFixed(6)}`;
        const addr = await reverseGeocode(latitude, longitude);
        setDelivery((d) => ({ ...d, address: addr ?? coords, lat: latitude, lng: longitude }));
        toast.show(addr ? 'Location added' : 'Got your location · address lookup unavailable', addr ? 'success' : 'info');
        setGeoBusy(false);
      },
      (err) => {
        setGeoBusy(false);
        toast.show(
          err.code === err.PERMISSION_DENIED
            ? 'Location permission denied — enter your address manually'
            : 'Could not get your location — enter your address manually',
          'error'
        );
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  // The customer moved the map pin → capture the point and follow it with a reverse-geocoded
  // address (which they can still edit by hand afterwards).
  const onPinMove = async (lat: number, lng: number) => {
    setDelivery((d) => ({ ...d, lat, lng }));
    const addr = await reverseGeocode(lat, lng);
    if (addr) setDelivery((d) => ({ ...d, address: addr, lat, lng }));
  };
  const waiter = useCallWaiter(tableCode, () => orderService.callWaiter(tableCode));
  const runningCount = orderSession.getCount(tableCode);
  const runningTotal = orderSession.getTotal(tableCode);
  const tableLabel = hasTable ? formatTable(tableCode) : 'Takeaway';
  // Dine-in only makes sense at a scanned table; without one, a dine-in selection becomes takeaway.
  const effectiveType: OrderType = hasTable ? orderType : orderType === 'dine-in' ? 'takeaway' : orderType;

  // Had no .catch, so a failure left the customer on "Loading table…" indefinitely.
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    setFailed(false);
    screenService
      .getQrMenu(tableCode)
      .then(async (d) => {
        setData(d);
        // Pre-select the category from the (encrypted) URL param — matched by id or
        // name, falling back to the first section.
        const cat = categoryParam ? await unprotectParam(categoryParam) : null;
        const wanted = cat
          ? d.categories.find((c) => c.id === cat || c.name.toLowerCase() === cat.toLowerCase())
          : undefined;
        // A QR/deep-link category focuses that one; otherwise default to "All".
        setCat(wanted ? wanted.id : 'all');
      })
      .catch(() => setFailed(true));
  }, [tableCode, categoryParam]);

  const visible = useMemo(
    () => (data ? data.items.filter((m) => m.category === cat) : []),
    [data, cat]
  );

  // One dish row — shared by the single-category filter and the stacked "All" view.
  const renderDish = (m: MenuItem) => {
    const qty = cart.qtyOf(m.id);
    return (
      <Card key={m.id} className='flex items-center gap-3 p-3'>
        <div
          onClick={() => setSelected(m)}
          className='flex min-w-0 flex-1 cursor-pointer items-center gap-3'
        >
          <div
            className='h-16 w-16 flex-shrink-0 rounded-xl'
            style={{ backgroundImage: `url(${m.image})`, backgroundSize: 'cover', backgroundPosition: 'center' }}
          />
          <div className='min-w-0 flex-1'>
            <div className='flex min-w-0 items-center gap-1.5 font-display text-[13px] font-bold'>
              <VegMark veg={m.veg} bare />
              <span className='truncate'>{m.name}</span>
            </div>
            <div className='mt-0.5 truncate text-[10.5px] text-content-muted'>{m.desc}</div>
            <PriceTag price={m.price} mrp={m.mrp} className='mt-1' />
          </div>
        </div>
        {qty > 0 ? (
          <QtyStepper qty={qty} onInc={() => cart.inc(m.id)} onDec={() => cart.dec(m.id)} />
        ) : (
          <Button variant='outline' size='sm' onClick={() => onAdd(m)}>
            {t('addToCart')}
          </Button>
        )}
      </Card>
    );
  };

  // Kitchen open/close status (day-wise hours) — refreshed periodically so the gate flips at
  // opening/closing time without a reload.
  useEffect(() => {
    let alive = true;
    const load = () => settingsService.kitchenStatus().then((k) => alive && setKitchen(k));
    load();
    const id = window.setInterval(load, 60000);
    return () => {
      alive = false;
      window.clearInterval(id);
    };
  }, []);

  function addToCart(m: MenuItem) {
    cart.add({ id: m.id, name: m.name, price: m.price, veg: m.veg, image: m.image });
  }

  function onAdd(m: MenuItem) {
    if (!kitchen.open) {
      toast.show(kitchen.message || 'The kitchen is closed right now', 'error');
      return;
    }
    // First order asks name + mobile (OTP); once signed in, add straight away.
    if (!hasCustomerDetails) {
      pendingRef.current = { action: 'add', item: m };
      setInfoOpen(true);
      return;
    }
    addToCart(m);
  }

  function placeOrder() {
    if (!kitchen.open) {
      toast.show(kitchen.message || 'The kitchen is closed right now', 'error');
      return;
    }
    // First order asks name + mobile (OTP) before it reaches the kitchen.
    if (!hasCustomerDetails) {
      pendingRef.current = { action: 'place' };
      setInfoOpen(true);
      return;
    }
    doPlaceOrder();
  }

  // Resume what the guest was doing once they've just signed in via the sheet. `customer`
  // is the freshly-verified name/phone (the redux `user` in scope is still the pre-login value).
  function resumeAfterSignIn(customer: { name?: string; phone?: string }) {
    setInfoOpen(false);
    const pending = pendingRef.current;
    pendingRef.current = null;
    if (!pending) return;
    if (pending.action === 'add' && pending.item) addToCart(pending.item);
    else if (pending.action === 'place') doPlaceOrder(customer);
  }

  async function doPlaceOrder(customer?: { name?: string; phone?: string }) {
    setSheetOpen(false);
    if (cart.count === 0) {
      toast.show('Add items first', 'error');
      return;
    }
    // HOME DELIVERY: collect address / contact / email BEFORE placing (gate before the cart is
    // touched, so nothing is lost if they still need to fill it in).
    if (effectiveType === 'delivery' && !delivery.address.trim()) {
      // Prefill digits only — the stored phone is display-formatted and the field itself is
      // capped at 10 digits, so "+91 98765…" would arrive truncated and wrong.
      setDelivery((d) => ({ ...d, contact: d.contact || orderPhone(customer?.phone || user?.phone || '') }));
      setDeliveryThenPlace(true); // opened from Place order → submit should place the order
      setDeliveryOpen(true);
      return;
    }
    const items = cart.items.map((i) => ({ name: i.name, qty: i.qty, price: i.price }));
    // Remember the delivery details (address + pin) so a returning customer sees them prefilled.
    if (effectiveType === 'delivery') {
      try {
        localStorage.setItem(
          'thalios.delivery',
          JSON.stringify({
            contact: delivery.contact.trim(),
            address: delivery.address.trim(),
            email: delivery.email.trim(),
            lat: delivery.lat,
            lng: delivery.lng
          })
        );
      } catch {
        /* storage full/blocked → prefill just won't persist */
      }
    }
    // The cart is emptied only AFTER the kitchen has the order.
    //
    // It used to be cleared here, before the call: a failed order left the customer looking at
    // "could not send your order" AND an empty cart, with every item to re-add by hand. The local
    // tracking session was started up front for the same reason, so a live "order in progress"
    // appeared for an order the kitchen had never received.
    const cartItems = cart.items;
    try {
      const placed = await orderService.place({
        // Empty when unscanned → the backend treats it as a takeaway (no phantom table).
        tableCode,
        type: effectiveType === 'packed' ? 'takeaway' : effectiveType,
        // Digits only. Sign-in returns the phone DISPLAY-formatted ("+91 9876543210") and the app
        // stores it that way, but the server requires a bare 10-digit number — so posting the
        // stored value back rejected the order with "Customer phone must be 10 digits starting
        // 6-9", which the screen reported as "Could not send your order to the kitchen".
        customerPhone: orderPhone(
          (effectiveType === 'delivery' && delivery.contact.trim()) || customer?.phone || user?.phone || ''
        ),
        customerName: customer?.name ?? user?.name ?? '',
        deliveryAddress: effectiveType === 'delivery' ? delivery.address.trim() : '',
        customerEmail: effectiveType === 'delivery' ? delivery.email.trim() : '',
        latitude: effectiveType === 'delivery' ? delivery.lat ?? 0 : 0,
        longitude: effectiveType === 'delivery' ? delivery.lng ?? 0 : 0,
        items
      });
      // Confirmed by the server: keep the local session for the live tracking view and empty
      // the cart.
      orderSession.placeOrder(tableCode, cartItems, effectiveType);
      cart.clear();
      toast.show(`Order #${placed.orderNo} sent to kitchen · ${placed.kot}`, 'success');
      navigate('/order/track');
    } catch (err) {
      // Never claim success when the order didn't reach the kitchen — the customer
      // would sit waiting for food nobody is cooking.
      const response = (err as { response?: { status?: number; data?: { message?: string } } })?.response;

      // A 401 here means the customer's session has gone (expired, or issued by an older build),
      // and the ONE thing that fixes it is signing in again. "Please try again or call a waiter"
      // sent them round the same loop for ever, because retrying without a session fails
      // identically. Their cart is untouched, so the order survives the re-sign-in.
      if (response?.status === 401) {
        toast.show('Please confirm your name and mobile to send this order', 'error');
        pendingRef.current = { action: 'place' };
        setInfoOpen(true);
        return;
      }

      // Otherwise show what the server actually said ("Kitchen closed…", "That table isn't
      // taking orders…") — it is always more useful than the generic line.
      const message = response?.data?.message;
      toast.show(
        message?.trim() || 'Could not send your order to the kitchen — please try again or call a waiter',
        'error'
      );
    }
  }

  // Order summary body — shared by the desktop side panel and the mobile order sheet.
  const renderSummary = () => (
    <>
      <div className='mb-2.5 font-display text-[13.5px] font-bold'>
        {runningCount > 0 ? 'Add to order' : t('yourOrder')} · {tableLabel}
      </div>
      {/* Order type · Dine-in (only at a scanned table) / Takeaway / Packed */}
      <div className='mb-2.5 flex rounded-lg border border-border p-0.5'>
        {ORDER_TYPES.filter((ot) => hasTable || ot.value !== 'dine-in').map((ot) => (
          <button
            key={ot.value}
            onClick={() => {
              setOrderType(ot.value);
              // Selecting Delivery immediately asks for the delivery address (a returning customer
              // with a saved address is left alone). Submit here just SAVES it (deliveryThenPlace=false).
              if (ot.value === 'delivery' && !delivery.address.trim()) {
                setDelivery((d) => ({ ...d, contact: d.contact || orderPhone(user?.phone || '') }));
                setDeliveryThenPlace(false);
                setDeliveryOpen(true);
              }
            }}
            className={cn(
              'flex-1 rounded-md py-1.5 text-[11px] font-bold transition',
              effectiveType === ot.value
                ? 'bg-primary text-primary-foreground'
                : 'text-content-secondary hover:text-content-primary'
            )}
          >
            {ot.label}
          </button>
        ))}
      </div>
      {/* Delivery: show the drop address inline so selecting Delivery has an immediate effect. */}
      {effectiveType === 'delivery' && (
        <button
          type='button'
          onClick={() => {
            setDeliveryThenPlace(false);
            setDeliveryOpen(true);
          }}
          className='mb-2.5 flex w-full items-center gap-2 rounded-lg border border-border bg-surface-card-hover px-3 py-2 text-left text-[11.5px]'
        >
          <MapPin size={13} className='flex-shrink-0 text-primary' />
          <span className='min-w-0 flex-1 truncate text-content-secondary'>
            {delivery.address.trim() ? delivery.address : 'Add delivery address'}
          </span>
          <span className='flex-shrink-0 font-bold text-primary'>{delivery.address.trim() ? 'Change' : 'Add →'}</span>
        </button>
      )}
      {runningCount > 0 && (
        <Link
          to='/order/track'
          className='mb-2.5 flex items-center justify-between rounded-lg bg-veg-light px-3 py-2 text-[11px] font-bold text-veg'
        >
          <span>
            ● Running order · {runningCount} item{runningCount > 1 ? 's' : ''}
          </span>
          <span>{format(runningTotal)} · View →</span>
        </Link>
      )}
      {cart.items.length === 0 ? (
        <p className='py-6 text-center text-[11.5px] text-content-muted'>
          {runningCount > 0 ? 'Pick more items to add to your order' : 'Your cart is empty'}
        </p>
      ) : (
        <>
          {cart.items.map((i) => (
            <div key={i.id} className='flex items-center justify-between gap-2 py-0.5 text-[11.5px]'>
              <span className='min-w-0 truncate text-content-secondary'>
                {i.qty}× {i.name}
              </span>
              <b className='flex-shrink-0'>{format(i.qty * i.price)}</b>
            </div>
          ))}
          <div className='mt-2 border-t border-dashed border-border pt-2' />
          {/* GST and coupons are applied at billing (Pay bill), not at order time. */}
          <Row label='Item total' value={format(cart.subTotal)} />
          <div className='mt-2 flex items-center justify-between border-t border-border pt-2'>
            <span className='font-display text-[13px] font-bold'>{t('toPay')}</span>
            <span className='font-display text-base font-extrabold text-primary'>
              {format(cart.subTotal)}
            </span>
          </div>
          <p className='mt-1 text-right text-[9.5px] text-content-muted'>Taxes &amp; offers applied on the final bill</p>
        </>
      )}
      <Button block size='lg' className='mt-3' onClick={placeOrder}>
        {t('placeOrder')} →
      </Button>
      <p className='mt-2 text-center text-[9.5px] text-content-muted'>
        Sent straight to kitchen · pay at end or now
      </p>
    </>
  );

  if (!data) {
    return (
      <div className='py-24 text-center text-sm text-content-muted'>
        {failed ? 'We couldn’t load the menu. Check your connection and try again.' : 'Loading table…'}
      </div>
    );
  }

  return (
    <div className='flex min-h-screen flex-col bg-surface-content pb-14 font-sans text-content-primary'>
      {/* Session header */}
      <header className='bg-primary text-white'>
        <div className='mx-auto flex w-full max-w-[1200px] flex-wrap items-center gap-x-3 gap-y-1.5 px-4 py-3'>
          <Link to='/' className='truncate font-display text-[15px] font-bold leading-none'>
            {data.restaurant || brand}
          </Link>
          <span className='h-4 w-px bg-white/25' aria-hidden />
          <div className='flex min-w-0 items-center gap-2'>
            <span className='truncate font-display text-sm font-bold'>
              {tableLabel}
            </span>
            {/* The "● Live" badge used to render unconditionally with no connection state
                behind it. OrderTrack gates its equivalent on `usingApi`; there is nothing
                comparable to gate on here, so it is gone rather than always-on. */}
          </div>
          <button
            onClick={waiter.call}
            disabled={waiter.disabled}
            className='ml-auto flex flex-shrink-0 items-center gap-1.5 rounded-full bg-white/15 px-3 py-1.5 font-display text-[11.5px] font-bold transition hover:bg-white/25 disabled:opacity-70'
          >
            <Bell size={13} /> {waiter.cooldown > 0 ? `Called · ${waiter.cooldown}s` : t('callWaiter')}
          </button>
        </div>
      </header>

      {/* Kitchen closed — the menu stays browsable but ordering is blocked with a message. */}
      {!kitchen.open && (
        <div className='mx-auto w-full max-w-[1200px] px-4 pt-3'>
          <div className='flex items-center gap-2 rounded-xl border border-warning/40 bg-warning-light px-4 py-3 text-[12.5px] font-semibold text-warning'>
            <Clock size={16} className='flex-shrink-0' />
            <span>{kitchen.message || 'The kitchen is closed right now.'}</span>
          </div>
        </div>
      )}

      <div className='mx-auto flex w-full max-w-[1200px] flex-1 flex-col gap-4 p-4 lg:flex-row'>
        {/* Category rail */}
        <aside className='flex gap-2 overflow-x-auto lg:w-[172px] lg:flex-col lg:gap-1'>
          <div className='hidden px-2.5 pb-2 text-[9.5px] font-bold uppercase tracking-wider text-content-muted lg:block'>
            Menu
          </div>
          {/* "All" shows every category one below another; the rest filter to one. */}
          <button
            onClick={() => setCat('all')}
            className={
              'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-[12.5px] transition ' +
              (cat === 'all'
                ? 'bg-primary-light font-bold text-primary'
                : 'text-content-secondary hover:bg-surface-card-hover')
            }
          >
            <Utensils size={16} className='flex-shrink-0' /> All
          </button>
          {data.categories.map((c) => {
            const Icon = CATEGORY_ICON[c.id] ?? Utensils;
            return (
              <button
                key={c.id}
                onClick={() => setCat(c.id)}
                className={
                  'flex items-center gap-2 whitespace-nowrap rounded-lg px-3 py-2.5 text-left text-[12.5px] transition ' +
                  (cat === c.id
                    ? 'bg-primary-light font-bold text-primary'
                    : 'text-content-secondary hover:bg-surface-card-hover')
                }
              >
                <Icon size={16} className='flex-shrink-0' /> {c.name}
              </button>
            );
          })}
        </aside>

        {/* Menu items */}
        <section className='min-w-0 flex-1'>
          <div className='mb-3 flex gap-2'>
            <Chip>
              <VegMark veg bare /> Veg only
            </Chip>
            <Chip>★ Chef's picks</Chip>
          </div>
          <div className='flex flex-col gap-2.5'>
            {cat === 'all'
              ? // "All" — every category one below another, each under its own heading.
                data.categories.map((c) => {
                  const items = data.items.filter((m) => m.category === c.id);
                  if (items.length === 0) return null;
                  return (
                    <div key={c.id} className='flex flex-col gap-2.5'>
                      <h3 className='mt-1.5 font-display text-[15px] font-bold text-content-primary'>{c.name}</h3>
                      {items.map(renderDish)}
                    </div>
                  );
                })
              : visible.map(renderDish)}
          </div>
        </section>

        {/* Live cart — desktop side panel (mobile uses the sticky button + sheet below) */}
        <aside className='hidden lg:block lg:w-[280px] lg:flex-shrink-0'>
          <Card className='p-3.5 lg:sticky lg:top-4'>{renderSummary()}</Card>
        </aside>
      </div>

      {/* Sticky place-order button — only the button shows while scrolling (mobile). */}
      {cart.count > 0 && !sheetOpen && (
        <div className='fixed inset-x-0 bottom-14 z-40 border-t border-border bg-surface-card px-4 py-2.5 shadow-pop lg:hidden'>
          <Button block size='lg' onClick={() => setSheetOpen(true)}>
            {t('placeOrder')} · {format(cart.subTotal)} →
          </Button>
        </div>
      )}

      {/* Mobile order sheet — tapping the button slides up the full card
          (Dine-in / Takeaway / Packed + summary). */}
      {sheetOpen && cart.count > 0 && (
        <div className='fixed inset-0 z-50 flex flex-col justify-end lg:hidden'>
          <button
            aria-label='Close'
            onClick={() => setSheetOpen(false)}
            className='absolute inset-0 bg-black/40'
          />
          <div className='relative max-h-[85vh] overflow-y-auto rounded-t-2xl border-t border-border bg-surface-card px-4 pb-6 pt-4 shadow-pop'>
            <div className='mx-auto mb-3 h-1 w-10 rounded-full bg-border' />
            <button
              aria-label='Close'
              onClick={() => setSheetOpen(false)}
              className='absolute right-3 top-3 rounded-full p-1 text-content-muted transition hover:bg-surface-card-hover'
            >
              <X size={20} />
            </button>
            {renderSummary()}
          </div>
        </div>
      )}

      <ItemDetailModal
        item={selected}
        onClose={() => setSelected(null)}
        onAdd={(qty, note) => {
          if (!selected) return;
          // Same first-order gate as the list "Add": prompt name+mobile if not signed in.
          if (!hasCustomerDetails) {
            pendingRef.current = { action: 'add', item: selected };
            setInfoOpen(true);
            return;
          }
          cart.add(
            {
              id: selected.id,
              name: selected.name,
              price: selected.price,
              veg: selected.veg,
              image: selected.image,
              note
            },
            qty
          );
          toast.show(`${selected.name} added`, 'success');
        }}
      />

      {/* First-order name + mobile capture (Petpooja flow) — only when not signed in. */}
      {/* Only the QR-scanner path (seated at a scanned table) skips OTP; takeaway keeps it. */}
      <CustomerInfoModal
        open={infoOpen}
        onClose={() => {
          setInfoOpen(false);
          pendingRef.current = null;
        }}
        onSuccess={resumeAfterSignIn}
        initialName={customerName.toLowerCase() === 'guest' ? '' : customerName}
        initialPhone={user?.phone ?? ''}
        skipOtp={hasTable}
      />

      {/* Home-delivery details — required before a delivery order is placed. */}
      {deliveryOpen && (
        <div
          className='fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center sm:p-4'
          onClick={() => setDeliveryOpen(false)}
        >
          <div
            className='max-h-[92vh] w-full max-w-md overflow-y-auto rounded-t-2xl bg-surface-card p-5 sm:rounded-2xl'
            onClick={(e) => e.stopPropagation()}
          >
            <div className='mb-1 font-display text-base font-bold'>Delivery details</div>
            <p className='mb-4 text-[11.5px] text-content-muted'>Where should we deliver your order?</p>
            <div className='mb-1 flex items-center justify-between'>
              <label className='block text-[10px] font-bold uppercase tracking-wide text-content-muted'>Delivery address *</label>
              <button
                type='button'
                onClick={useMyLocation}
                disabled={geoBusy}
                className='flex items-center gap-1 text-[11px] font-bold text-primary disabled:opacity-60'
              >
                <MapPin size={12} className={geoBusy ? 'animate-pulse' : ''} />
                {geoBusy ? 'Locating…' : 'Use my location'}
              </button>
            </div>
            {/* Interactive drop-point picker — drag the pin or tap the map to set the exact spot. */}
            <MapPicker lat={delivery.lat} lng={delivery.lng} onChange={onPinMove} />
            {delivery.lat != null && delivery.lng != null && (
              <div className='mb-3 flex items-center gap-1 text-[10.5px] text-content-muted'>
                <MapPin size={11} className='text-primary' />
                Pin set · {delivery.lat.toFixed(5)}, {delivery.lng.toFixed(5)}
              </div>
            )}
            <div className='mb-3'>
              <textarea
                value={delivery.address}
                onChange={(e) => setDelivery((d) => ({ ...d, address: applyGuard(e.target.value, 'text', 250) }))}
                onBlur={addressField.onBlur}
                rows={3}
                maxLength={250}
                placeholder='House / flat, street, area, landmark, PIN'
                aria-label='Delivery address'
                aria-invalid={addressField.error ? true : undefined}
                className={cn(
                  'w-full resize-none rounded-lg border bg-surface-card-hover px-3 py-2.5 text-[13px] outline-none',
                  addressField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                )}
              />
              <FieldError error={addressField.error} />
            </div>
            <label className='mb-1 block text-[10px] font-bold uppercase tracking-wide text-content-muted'>
              Contact number<span className='text-danger'> *</span>
            </label>
            <div className='mb-3'>
              <input
                value={delivery.contact}
                onChange={(e) => setDelivery((d) => ({ ...d, contact: onlyDigits(e.target.value).slice(0, 10) }))}
                onBlur={contactField.onBlur}
                inputMode='tel'
                maxLength={10}
                placeholder='10-digit mobile'
                aria-label='Contact number'
                aria-invalid={contactField.error ? true : undefined}
                className={cn(
                  'w-full rounded-lg border bg-surface-card-hover px-3 py-2.5 text-[13px] outline-none',
                  contactField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                )}
              />
              <FieldError error={contactField.error} />
            </div>
            <label className='mb-1 block text-[10px] font-bold uppercase tracking-wide text-content-muted'>
              Email <span className='font-medium normal-case tracking-normal text-content-muted/70'>(optional)</span>
            </label>
            <div className='mb-4'>
              <input
                value={delivery.email}
                onChange={(e) => setDelivery((d) => ({ ...d, email: e.target.value.trim().slice(0, 120) }))}
                onBlur={emailField.onBlur}
                type='email'
                maxLength={120}
                placeholder='you@example.com'
                aria-label='Email'
                aria-invalid={emailField.error ? true : undefined}
                className={cn(
                  'w-full rounded-lg border bg-surface-card-hover px-3 py-2.5 text-[13px] outline-none',
                  emailField.error ? 'border-danger focus:border-danger' : 'border-border focus:border-primary'
                )}
              />
              <FieldError error={emailField.error} />
            </div>
            <div className='flex gap-2'>
              <Button variant='outline' className='flex-1' onClick={() => setDeliveryOpen(false)}>
                Cancel
              </Button>
              <Button
                className='flex-1'
                disabled={!deliveryValid}
                onClick={() => {
                  setDeliveryOpen(false);
                  if (deliveryThenPlace) {
                    setDeliveryThenPlace(false);
                    doPlaceOrder();
                  }
                }}
              >
                {deliveryThenPlace ? 'Place delivery order' : 'Save address'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Row({
  label,
  value,
  accent
}: {
  label: string;
  value: string;
  accent?: 'veg';
}) {
  return (
    <div
      className={
        'flex justify-between py-0.5 text-[11px] ' +
        (accent === 'veg' ? 'font-bold text-veg' : 'text-content-secondary')
      }
    >
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
